import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ShoppingCart, LogOut, Settings as SettingsIcon, Plus, Minus, Trash2, Check, X, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

// Componente de Notificação
const Notification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-[#FF5500]';

  return (
    <div className={`notification-toast fixed top-4 left-1/2 transform -translate-x-1/2 z-50 ${bgColor} text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-slide-down`}>
      {type === 'success' && <Check className="w-5 h-5" />}
      {type === 'error' && <X className="w-5 h-5" />}
      <span className="font-oswald font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Componente de impressão
const PrintTicket = ({ ticketData, onClose }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (ticketData && iframeRef.current) {
      const printValues = ticketData.print_values !== false;
      
      const ticketHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            @page { size: 80mm auto; margin: 0; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; font-size: 12px; width: 80mm; padding: 3mm; background: white; color: black; }
            .center { text-align: center; }
            .establishment { font-size: 16px; font-weight: bold; text-align: center; margin-bottom: 2mm; }
            .table-number { text-align: center; font-size: 14px; margin-bottom: 2mm; border: 1px solid black; padding: 2mm; }
            .divider { border-top: 1px dashed #000; margin: 2mm 0; }
            .info-line { margin: 1mm 0; }
            .item { margin: 1mm 0; }
            .item-detail { padding-left: 3mm; color: #333; }
            .total { font-size: 14px; font-weight: bold; margin: 2mm 0; }
            .payment { text-align: center; font-size: 12px; margin: 2mm 0; }
            .footer { text-align: center; margin-top: 3mm; }
          </style>
        </head>
        <body>
          <div class="establishment">${ticketData.establishment || 'ESTABELECIMENTO'}</div>
          ${ticketData.table_number ? `<div class="table-number">Mesa: ${ticketData.table_number}</div>` : ''}
          ${ticketData.custom_message ? `<div class="center">${ticketData.custom_message}</div>` : ''}
          <div class="divider"></div>
          <div class="info-line">Data: ${ticketData.date} ${ticketData.time}</div>
          <div class="info-line">Vendedor: ${ticketData.seller || '-'}</div>
          <div class="divider"></div>
          ${ticketData.items.map(item => `
            <div class="item">
              <div>${item.name}</div>
              <div class="item-detail">
                ${printValues 
                  ? `${item.quantity}x ${ticketData.currency_symbol} ${item.price.toFixed(2)} = ${ticketData.currency_symbol} ${item.subtotal.toFixed(2)}`
                  : `Qtd: ${item.quantity}`
                }
              </div>
            </div>
          `).join('')}
          <div class="divider"></div>
          ${printValues ? `<div class="total center">TOTAL: ${ticketData.currency_symbol} ${ticketData.total.toFixed(2)}</div>` : ''}
          ${ticketData.payment_method ? `<div class="payment">Pagamento: ${ticketData.payment_method}</div>` : ''}
          <div class="divider"></div>
          <div class="footer">Obrigado!</div>
        </body>
        </html>
      `;

      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(ticketHtml);
      doc.close();

      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          window.print();
        }
        setTimeout(onClose, 500);
      }, 200);
    }
  }, [ticketData, onClose]);

  if (!ticketData) return null;

  return (
    <iframe
      ref={iframeRef}
      style={{ position: 'fixed', left: '-9999px', top: 0, width: '80mm', height: '100%', border: 'none' }}
      title="print-frame"
    />
  );
};

// Modal de Pagamento
const PaymentModal = ({ isOpen, onClose, onConfirm, total, currencySymbol, paymentMethods }) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const methods = paymentMethods ? paymentMethods.split(',').map(m => m.trim()).filter(m => m) : [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#18181b] rounded-2xl w-full max-w-md p-6 border border-white/10">
        <h2 className="text-2xl font-oswald font-bold text-white mb-2">Forma de Pagamento</h2>
        <p className="text-white/60 mb-6">Total: <span className="text-[#FF5500] font-bold text-xl">{currencySymbol} {total.toFixed(2)}</span></p>
        
        <div className="grid grid-cols-1 gap-3 mb-6">
          {methods.map(method => (
            <button
              key={method}
              onClick={() => setSelectedMethod(method)}
              className={`p-4 rounded-xl font-oswald text-lg transition-all ${
                selectedMethod === method
                  ? 'bg-[#FF5500] text-white'
                  : 'bg-white/5 text-white hover:bg-white/10 border border-white/10'
              }`}
            >
              {method}
            </button>
          ))}
        </div>
        
        <div className="flex gap-3">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
            Cancelar
          </Button>
          <Button 
            onClick={() => selectedMethod && onConfirm(selectedMethod)} 
            disabled={!selectedMethod}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          >
            <Check className="w-4 h-4 mr-2" />
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Modal de Mesa
const TableModal = ({ isOpen, onClose, onConfirm }) => {
  const [tableNumber, setTableNumber] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#18181b] rounded-2xl w-full max-w-md p-6 border border-white/10">
        <h2 className="text-2xl font-oswald font-bold text-white mb-4">Número da Mesa</h2>
        
        <input
          type="text"
          value={tableNumber}
          onChange={(e) => setTableNumber(e.target.value)}
          placeholder="Ex: 1, A1, VIP..."
          className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-white font-oswald text-xl text-center mb-6"
          autoFocus
        />
        
        <div className="flex gap-3">
          <Button onClick={() => { setTableNumber(''); onConfirm(''); }} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
            Pular
          </Button>
          <Button 
            onClick={() => { onConfirm(tableNumber); setTableNumber(''); }}
            className="flex-1 bg-[#FF5500] hover:bg-[#CC4400] text-white"
          >
            <Check className="w-4 h-4 mr-2" />
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POS = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [ticketToPrint, setTicketToPrint] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [currentTableNumber, setCurrentTableNumber] = useState('');
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchData();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações');
    }
  };

  const fetchData = async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`)
      ]);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
      if (categoriesRes.data.length > 0) {
        setSelectedCategory(categoriesRes.data[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory
    ? products.filter(p => p.category_id === selectedCategory)
    : products;

  const addToCart = (product) => {
    // Se é o primeiro item e ask_table_number está ativo, pedir mesa
    if (cart.length === 0 && settings.ask_table_number && !currentTableNumber) {
      setShowTableModal(true);
    }
    
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const handleTableConfirm = (tableNum) => {
    setCurrentTableNumber(tableNum);
    setShowTableModal(false);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.id === productId) {
        const newQuantity = item.quantity + delta;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const getCurrencySymbol = () => {
    const curr = settings.currency || 'BRL';
    return {'BRL': 'R$', 'EUR': '€', 'USD': '$'}[curr] || 'R$';
  };

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      showNotification('Carrinho vazio!', 'error');
      return;
    }
    setShowPaymentModal(true);
  };

  const completeCheckout = async (paymentMethod) => {
    setShowPaymentModal(false);
    
    try {
      const saleData = {
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        total,
        payment_method: paymentMethod,
        table_number: currentTableNumber || null
      };

      const response = await axios.post(`${API}/sales`, saleData);
      const sale = response.data;

      const printResponse = await axios.post(`${API}/print`, { sale_id: sale.id });
      
      if (printResponse.data.printer_type === 'windows' && printResponse.data.ticket_data) {
        printResponse.data.ticket_data.payment_method = paymentMethod;
        printResponse.data.ticket_data.table_number = currentTableNumber;
        setTicketToPrint(printResponse.data.ticket_data);
      }
      
      setCart([]);
      setCurrentTableNumber('');
      setShowCart(false);
      showNotification(`Venda ${getCurrencySymbol()} ${total.toFixed(2)} - ${paymentMethod}`, 'success');
    } catch (error) {
      console.error('Erro ao finalizar venda');
      showNotification('Erro ao finalizar venda!', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-white font-oswald text-2xl">Carregando...</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print { .notification-toast, .no-print { display: none !important; } }
        @keyframes slide-down { from { transform: translate(-50%, -100%); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
        .animate-slide-down { animation: slide-down 0.3s ease-out; }
      `}</style>

      {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      {ticketToPrint && <PrintTicket ticketData={ticketToPrint} onClose={() => setTicketToPrint(null)} />}
      
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={completeCheckout}
        total={total}
        currencySymbol={getCurrencySymbol()}
        paymentMethods={settings.payment_methods}
      />
      
      <TableModal
        isOpen={showTableModal}
        onClose={() => setShowTableModal(false)}
        onConfirm={handleTableConfirm}
      />

      {/* Layout Desktop e Tablet */}
      <div className="hidden md:grid grid-cols-12 h-screen overflow-hidden bg-[#09090b] no-print">
        <div className="col-span-8 lg:col-span-8 p-4 lg:p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-4 lg:mb-6">
            <div>
              <h1 className="text-2xl lg:text-4xl font-bold font-oswald text-white">Vendas</h1>
              <div className="flex items-center gap-2">
                <p className="text-white/60 font-inter text-sm lg:text-base">Olá, {user?.name}</p>
                {currentTableNumber && (
                  <span className="bg-[#FF5500]/20 text-[#FF5500] px-2 py-0.5 rounded text-sm flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    Mesa {currentTableNumber}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 lg:gap-3">
              {user?.role === 'admin' && (
                <Button
                  data-testid="admin-panel-button"
                  onClick={() => navigate('/admin')}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/10 text-sm lg:text-base"
                >
                  <SettingsIcon className="w-4 h-4 lg:mr-2" />
                  <span className="hidden lg:inline">Admin</span>
                </Button>
              )}
              <Button
                data-testid="logout-button"
                onClick={() => { logout(); navigate('/login'); }}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 text-sm lg:text-base"
              >
                <LogOut className="w-4 h-4 lg:mr-2" />
                <span className="hidden lg:inline">Sair</span>
              </Button>
            </div>
          </div>

          <div className="flex gap-2 lg:gap-4 mb-4 lg:mb-6 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(category => (
              <button
                key={category.id}
                data-testid={`category-${category.name}`}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 lg:px-6 py-2 lg:py-3 rounded-full font-oswald font-medium transition-all whitespace-nowrap text-sm lg:text-base ${
                  selectedCategory === category.id
                    ? 'bg-[#FF5500] text-white shadow-lg scale-105'
                    : 'bg-[#18181b] text-white/70 hover:bg-[#27272a] border border-white/5'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
            {filteredProducts.map(product => (
              <div
                key={product.id}
                data-testid={`product-card-${product.name}`}
                onClick={() => addToCart(product)}
                className="relative group overflow-hidden rounded-xl bg-[#18181b] border border-white/5 hover:border-[#FF5500]/50 transition-all duration-300 active:scale-95 cursor-pointer"
              >
                {product.image_path ? (
                  <img
                    src={`${API}/files/${product.image_path}?auth=${localStorage.getItem('token')}`}
                    alt={product.name}
                    className="h-28 lg:h-40 w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="h-28 lg:h-40 w-full bg-gradient-to-br from-[#27272a] to-[#18181b] flex items-center justify-center">
                    <ShoppingCart className="w-8 lg:w-12 h-8 lg:h-12 text-white/20" />
                  </div>
                )}
                <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-md text-white px-2 lg:px-3 py-1 rounded-full font-oswald font-bold border border-white/10 text-xs lg:text-sm">
                  {getCurrencySymbol()} {product.price.toFixed(2)}
                </div>
                <div className="p-3 lg:p-4">
                  <h3 className="font-oswald font-semibold text-white text-sm lg:text-lg mb-1 truncate">{product.name}</h3>
                  <p className="text-white/60 text-xs lg:text-sm font-inter line-clamp-2">{product.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Carrinho Desktop */}
        <div className="col-span-4 bg-[#18181b] border-l border-white/10 flex flex-col h-full shadow-2xl">
          <div className="p-4 lg:p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-5 lg:w-6 h-5 lg:h-6 text-[#FF5500]" />
              <h2 className="text-xl lg:text-2xl font-oswald font-bold text-white">Carrinho</h2>
            </div>
            <p className="text-white/60 text-sm mt-1 font-inter">{cartItemsCount} itens</p>
          </div>

          <div className="flex-1 overflow-y-auto p-3 lg:p-4 space-y-2 lg:space-y-3">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-white/40">
                <ShoppingCart className="w-12 lg:w-16 h-12 lg:h-16 mb-4" />
                <p className="font-inter text-sm lg:text-base">Carrinho vazio</p>
              </div>
            ) : (
              cart.map(item => (
                <div key={item.id} data-testid={`cart-item-${item.name}`} className="flex justify-between items-center p-2 lg:p-3 rounded-lg bg-white/5 border border-white/5">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-oswald font-medium text-white text-sm lg:text-base truncate">{item.name}</h4>
                    <p className="text-xs lg:text-sm text-white/60 font-inter">{getCurrencySymbol()} {item.price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1 lg:gap-2">
                    <button onClick={() => updateQuantity(item.id, -1)} className="w-7 lg:w-8 h-7 lg:h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all">
                      <Minus className="w-3 lg:w-4 h-3 lg:h-4 text-white" />
                    </button>
                    <span className="w-6 lg:w-8 text-center font-oswald font-bold text-white text-sm lg:text-base">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, 1)} className="w-7 lg:w-8 h-7 lg:h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all">
                      <Plus className="w-3 lg:w-4 h-3 lg:h-4 text-white" />
                    </button>
                    <button onClick={() => removeFromCart(item.id)} className="w-7 lg:w-8 h-7 lg:h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center transition-all ml-1 lg:ml-2">
                      <Trash2 className="w-3 lg:w-4 h-3 lg:h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 lg:p-6 bg-[#09090b] border-t border-white/10 space-y-3 lg:space-y-4">
            <div className="flex justify-between items-center">
              <span className="font-oswald text-lg lg:text-xl text-white">TOTAL</span>
              <span className="font-oswald text-2xl lg:text-3xl font-bold text-[#FF5500]" data-testid="cart-total">
                {getCurrencySymbol()} {total.toFixed(2)}
              </span>
            </div>
            <Button
              data-testid="checkout-button"
              onClick={handleCheckout}
              disabled={cart.length === 0}
              className="w-full bg-[#FF5500] hover:bg-[#CC4400] text-white font-oswald text-base lg:text-lg h-12 lg:h-14 rounded-xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-5 h-5 mr-2" />
              Finalizar Venda
            </Button>
          </div>
        </div>
      </div>

      {/* Layout Mobile */}
      <div className="md:hidden flex flex-col h-screen bg-[#09090b] no-print">
        <div className="flex justify-between items-center p-4 border-b border-white/10">
          <div>
            <h1 className="text-xl font-bold font-oswald text-white">Vendas</h1>
            <div className="flex items-center gap-2">
              <p className="text-white/60 font-inter text-xs">Olá, {user?.name}</p>
              {currentTableNumber && (
                <span className="bg-[#FF5500]/20 text-[#FF5500] px-1.5 py-0.5 rounded text-xs flex items-center">
                  <Users className="w-3 h-3 mr-1" />
                  {currentTableNumber}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button onClick={() => navigate('/admin')} size="sm" className="bg-white/10 hover:bg-white/20 text-white border border-white/10 h-9 w-9 p-0">
                <SettingsIcon className="w-4 h-4" />
              </Button>
            )}
            <Button onClick={() => { logout(); navigate('/login'); }} size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 h-9 w-9 p-0">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full font-oswald font-medium transition-all whitespace-nowrap text-sm ${
                selectedCategory === category.id ? 'bg-[#FF5500] text-white' : 'bg-[#18181b] text-white/70 border border-white/5'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3">
            {filteredProducts.map(product => (
              <div
                key={product.id}
                onClick={() => addToCart(product)}
                className="relative overflow-hidden rounded-xl bg-[#18181b] border border-white/5 active:scale-95 transition-all cursor-pointer"
              >
                {product.image_path ? (
                  <img src={`${API}/files/${product.image_path}?auth=${localStorage.getItem('token')}`} alt={product.name} className="h-24 w-full object-cover" />
                ) : (
                  <div className="h-24 w-full bg-gradient-to-br from-[#27272a] to-[#18181b] flex items-center justify-center">
                    <ShoppingCart className="w-8 h-8 text-white/20" />
                  </div>
                )}
                <div className="absolute top-2 right-2 bg-black/80 text-white px-2 py-0.5 rounded-full font-oswald font-bold text-xs">
                  {getCurrencySymbol()} {product.price.toFixed(2)}
                </div>
                <div className="p-2">
                  <h3 className="font-oswald font-semibold text-white text-sm truncate">{product.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#18181b] border-t border-white/10 p-4" onClick={() => cart.length > 0 && setShowCart(true)}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="relative">
                <ShoppingCart className="w-6 h-6 text-[#FF5500]" />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#FF5500] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {cartItemsCount}
                  </span>
                )}
              </div>
              <span className="text-white font-oswald">{cartItemsCount} itens</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-oswald text-xl font-bold text-[#FF5500]">{getCurrencySymbol()} {total.toFixed(2)}</span>
              <Button onClick={(e) => { e.stopPropagation(); handleCheckout(); }} disabled={cart.length === 0} className="bg-[#FF5500] hover:bg-[#CC4400] text-white font-oswald h-10 px-4 rounded-xl disabled:opacity-50">
                <Check className="w-4 h-4 mr-1" />
                Pagar
              </Button>
            </div>
          </div>
        </div>

        {showCart && (
          <div className="fixed inset-0 bg-black/80 z-50 flex flex-col no-print">
            <div className="flex justify-between items-center p-4 border-b border-white/10 bg-[#18181b]">
              <h2 className="text-xl font-oswald font-bold text-white">Carrinho</h2>
              <button onClick={() => setShowCart(false)} className="text-white/60"><X className="w-6 h-6" /></button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 bg-[#09090b] space-y-3">
              {cart.map(item => (
                <div key={item.id} className="flex justify-between items-center p-3 rounded-lg bg-[#18181b] border border-white/5">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-oswald font-medium text-white truncate">{item.name}</h4>
                    <p className="text-sm text-white/60 font-inter">{getCurrencySymbol()} {item.price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(item.id, -1)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <Minus className="w-4 h-4 text-white" />
                    </button>
                    <span className="w-8 text-center font-oswald font-bold text-white">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, 1)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <Plus className="w-4 h-4 text-white" />
                    </button>
                    <button onClick={() => removeFromCart(item.id)} className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center ml-2">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 bg-[#18181b] border-t border-white/10 space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-oswald text-xl text-white">TOTAL</span>
                <span className="font-oswald text-2xl font-bold text-[#FF5500]">{getCurrencySymbol()} {total.toFixed(2)}</span>
              </div>
              <Button onClick={handleCheckout} disabled={cart.length === 0} className="w-full bg-[#FF5500] hover:bg-[#CC4400] text-white font-oswald text-lg h-14 rounded-xl">
                <Check className="w-5 h-5 mr-2" />
                Finalizar Venda
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default POS;
